import React, { useCallback, useMemo } from 'react';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import {
  profileImageSpec,
  profileSourcesOf,
  type ArtistProfileKind,
  type ArtistProfileSource,
} from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { HeaderSourceTiles, type SourceTileNames } from '../../studio/spotifyHeader/HeaderSourceTiles';
import { ProfileImageFrame } from '../frame/ProfileImageFrame';
import { profileToolMessages as t } from './profileToolMessages';

const NAMES: Readonly<Record<ArtistProfileSource, SourceTileNames>> = {
  cover: { name: t.sourceCover, benefit: t.sourceCoverBenefit },
  photo: { name: t.sourcePhoto, benefit: t.sourcePhotoBenefit },
  bank: { name: t.sourceBank, benefit: t.sourceBankBenefit },
  ai: { name: t.sourceAi, benefit: t.sourceAiBenefit },
  theme: { name: t.sourceTheme, benefit: t.sourceThemeBenefit },
};

/** The cover of a photo kind is drawn as it is: "Release cover" (design b3). */
const PLAIN_COVER: SourceTileNames = { name: t.sourceCoverPlain, benefit: t.sourceCoverPlainBenefit };

interface ProfileSourceTilesProps {
  kind: ArtistProfileKind;
  value: ArtistProfileSource;
  colours: ThemeColours;
  grounds: Readonly<Record<ArtistProfileSource, HeaderPreviewGround>>;
  hint: string;
  onChange: (source: ArtistProfileSource) => void;
}

/**
 * "Picture" of a Profile tool (design b1–b4, d2): the sources of the kind (`profileSourcesOf`) as
 * the tiles of the Spotify header step. The header keeps its small header well; every other kind
 * draws a small frame of its own file.
 */
const ProfileSourceTilesComponent: React.FC<ProfileSourceTilesProps> = ({
  kind,
  value,
  colours,
  grounds,
  hint,
  onChange,
}) => {
  const spec = profileImageSpec(kind);
  const sources = profileSourcesOf(kind);
  const names = useMemo(
    () => (spec.coverMode === 'plain' ? { ...NAMES, cover: PLAIN_COVER } : NAMES),
    [spec.coverMode]
  );
  const renderPreview = useCallback(
    (source: ArtistProfileSource, narrow: boolean) => {
      const square = spec.width === spec.height;
      const width = square ? 56 : narrow ? 136 : 116;
      return (
        <ProfileImageFrame
          spec={spec}
          width={width}
          ground={grounds[source]}
          colours={colours}
          round={kind === 'spotifyAvatar'}
        />
      );
    },
    [colours, grounds, kind, spec]
  );
  return (
    <HeaderSourceTiles
      value={value}
      colours={colours}
      grounds={grounds}
      sources={sources}
      names={names}
      hint={hint}
      renderPreview={kind === 'spotifyHeader' ? undefined : renderPreview}
      testIdPrefix="profile-source"
      onChange={onChange}
    />
  );
};

export const ProfileSourceTiles = TypedMemo(ProfileSourceTilesComponent);
